import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Modal, useToast } from '../../components/primitives';
import { SignatureLibraryModal } from '../../components/SignatureLibrary';
import { api } from '../../lib/api';
import type { SavedDocument } from '../../api/schemas';
import {
  CHAIN_LABEL,
  chainPath,
  getDoc,
  invoiceRelatedDocs,
  isChainDocType,
  type ChainRef,
} from '../../lib/docChain';
import { blobToBase64, downloadBlob, htmlToPdfBlob, mergePdfs, savedDocPdf } from '../../lib/docBundle';
import { toRealSignature } from '../../lib/signatureImage';
import { safeFileName } from '../../lib/safeFileName';

/**
 * Přílohy faktury z řetězce dokladů: soupis prací, zjišťovací protokol,
 * předávací protokol díla (a volitelně nabídka/objednávka). Posílají se
 * jako samostatná PDF, nebo sloučená s fakturou do jednoho PDF.
 */

/** Pořadí příloh za fakturou. */
const ORDER: Record<string, number> = {
  zjistovaci: 1, soupis: 2, 'predavaci-dilo': 3, objednavka: 4, nabidka: 5,
};
/** Co je standardně zaškrtnuté — nabídka/objednávka se k faktuře obvykle neposílá. */
const DEFAULT_ON = new Set(['zjistovaci', 'soupis', 'predavaci-dilo']);
/** Kam se při „Podepsat vše" vloží podpis zhotovitele. */
const SIGNABLE = new Set(['zjistovaci', 'soupis', 'predavaci-dilo']);

/** Rozměr podpisového pole v dokumentech (SignaturePad 400×110). */
const PAD_ASPECT = 400 / 110;

type Obj = Record<string, unknown>;
const o = (v: unknown): Obj => (v && typeof v === 'object' ? (v as Obj) : {});

function withZhotovitelSignature(docType: string, data: Obj, sig: string): Obj {
  const d = { ...data };
  if (docType === 'predavaci-dilo') {
    d.signatures = { ...o(d.signatures), zhotovitel: sig };
  } else {
    const p = o(d.podpisy);
    d.podpisy = { ...p, zhotovitel: { jmeno: '', funkce: '', ...o(p.zhotovitel), image: sig } };
  }
  return d;
}

export function useInvoiceBundle(zdroj: ChainRef | null) {
  const [docs, setDocs] = useState<SavedDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [merged, setMerged] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!zdroj) {
      setDocs([]);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    invoiceRelatedDocs(zdroj)
      .then((list) => {
        if (!alive) return;
        const sorted = list
          .filter((d) => d.doc_type !== 'faktura')
          .sort((a, b) => (ORDER[a.doc_type] ?? 9) - (ORDER[b.doc_type] ?? 9));
        setDocs(sorted);
        setSelected((prev) =>
          reload > 0 ? prev : new Set(sorted.filter((d) => DEFAULT_ON.has(d.doc_type)).map((d) => d.id)),
        );
      })
      .catch(() => alive && setError('Navazující doklady se nepodařilo načíst.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zdroj?.id, reload]);

  const chosen = useMemo(() => docs.filter((d) => selected.has(d.id)), [docs, selected]);

  async function attachmentPdfs(): Promise<Array<{ blob: Blob; filename: string }>> {
    const out: Array<{ blob: Blob; filename: string }> = [];
    for (const d of chosen) {
      try {
        out.push(await savedDocPdf(d, chainPath(d.doc_type)));
      } catch (e) {
        throw new Error(`Přílohu „${d.title}" se nepodařilo připravit: ${e instanceof Error ? e.message : 'chyba'}`);
      }
    }
    return out;
  }

  return {
    docs,
    loading,
    error,
    selected,
    chosen,
    toggle(id: number) {
      setSelected((s) => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      });
    },
    merged,
    setMerged,
    refresh: () => setReload((r) => r + 1),
    /** Samostatné přílohy pro e-mail (base64). */
    async extraAttachments() {
      const pdfs = await attachmentPdfs();
      return Promise.all(
        pdfs.map(async (p) => ({ base64: await blobToBase64(p.blob), filename: p.filename, mime_type: 'application/pdf' })),
      );
    },
    /** Faktura + vybrané přílohy sloučené do jednoho PDF. */
    async mergedPdf(invoiceHtml: string, invoiceTitle: string): Promise<{ blob: Blob; filename: string }> {
      const fa = await htmlToPdfBlob(invoiceHtml, invoiceTitle);
      const rest = await attachmentPdfs();
      const blob = await mergePdfs([fa, ...rest.map((r) => r.blob)]);
      return { blob, filename: safeFileName(`${invoiceTitle}_s_prilohami`) };
    },
  };
}

export type InvoiceBundle = ReturnType<typeof useInvoiceBundle>;

export function InvoiceBundlePanel({
  bundle,
  buildInvoiceHtml,
  invoiceTitle,
  onSignInvoice,
}: {
  bundle: InvoiceBundle;
  buildInvoiceHtml: () => Promise<string>;
  invoiceTitle: string;
  /** Vloží podpis do faktury samotné (pole „Vystavil"). */
  onSignInvoice: (sig: string) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [pendingSig, setPendingSig] = useState<string | null>(null);

  const toSign = bundle.chosen.filter((d) => SIGNABLE.has(d.doc_type));

  async function downloadMerged() {
    setBusy('Připravuji PDF balík…');
    try {
      const { blob, filename } = await bundle.mergedPdf(await buildInvoiceHtml(), invoiceTitle);
      downloadBlob(blob, filename);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'PDF balík se nepodařilo připravit.');
    } finally {
      setBusy(null);
    }
  }

  async function pickSignature(dataUrl: string) {
    setPickOpen(false);
    try {
      setPendingSig(await toRealSignature(dataUrl, PAD_ASPECT));
    } catch {
      toast.error('Podpis se nepodařilo připravit.');
    }
  }

  async function signAll() {
    const sig = pendingSig;
    if (!sig) return;
    setPendingSig(null);
    setBusy('Podepisuji…');
    onSignInvoice(sig);
    const failed: string[] = [];
    for (const d of toSign) {
      try {
        // Čerstvá verze z archivu — ať se nepřepíše mezitím provedená úprava.
        const cur = await getDoc(d.id);
        await api.put<SavedDocument>(`/documents/${d.id}`, {
          doc_type: cur.doc_type,
          title: cur.title,
          stavba: cur.stavba ?? '',
          firma: cur.firma ?? '',
          data_json: withZhotovitelSignature(cur.doc_type, cur.data_json ?? {}, sig),
        });
      } catch {
        failed.push(d.title);
      }
    }
    void qc.invalidateQueries({ queryKey: ['documents'] });
    bundle.refresh();
    setBusy(null);
    if (failed.length) {
      toast.error(`Nepodepsáno: ${failed.join(', ')}. Ostatní doklady jsou podepsané.`);
    } else {
      toast.success(
        `Podepsáno: faktura${toSign.length ? ` + ${toSign.length} ${toSign.length === 1 ? 'doklad' : 'doklady'}` : ''}. Fakturu ještě ulož.`,
      );
    }
  }

  return (
    <div
      className="docNoPrint"
      style={{
        margin: '0 0 12px',
        padding: '10px 14px',
        borderRadius: 8,
        border: '1px solid var(--sd-border)',
        background: 'var(--sd-surface-2)',
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>📎 Přílohy k faktuře z řetězce dokladů</div>
      {bundle.loading && <div style={{ color: 'var(--sd-text-dim)' }}>Načítám navazující doklady…</div>}
      {bundle.error && <div style={{ color: 'var(--sd-danger)' }}>{bundle.error}</div>}
      {!bundle.loading && !bundle.error && bundle.docs.length === 0 && (
        <div style={{ color: 'var(--sd-text-dim)' }}>K faktuře nejsou v archivu žádné navazující doklady.</div>
      )}
      {bundle.docs.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {bundle.docs.map((d) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Checkbox
                  checked={bundle.selected.has(d.id)}
                  onChange={() => bundle.toggle(d.id)}
                  label={`${isChainDocType(d.doc_type) ? CHAIN_LABEL[d.doc_type] : d.doc_type}: ${d.title}`}
                />
                <Link
                  to={`${chainPath(d.doc_type)}?id=${d.id}`}
                  style={{ color: 'var(--sd-yellow-text)', fontSize: 12 }}
                >
                  otevřít
                </Link>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', marginBottom: 8 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={!bundle.merged} onChange={() => bundle.setMerged(false)} />
              E-mail: samostatná PDF
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={bundle.merged} onChange={() => bundle.setMerged(true)} />
              E-mail: vše sloučit do jednoho PDF
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => void downloadMerged()}>
              ⬇ Stáhnout fakturu s přílohami (1 PDF)
            </Button>
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => setPickOpen(true)}>
              ✍ Podepsat vše
            </Button>
            {busy && <span style={{ alignSelf: 'center', color: 'var(--sd-text-dim)' }}>{busy}</span>}
          </div>
        </>
      )}

      <SignatureLibraryModal open={pickOpen} onClose={() => setPickOpen(false)} onPick={(p) => void pickSignature(p.data_url)} />

      {pendingSig && (
        <Modal
          open
          onClose={() => setPendingSig(null)}
          title="Podepsat vše"
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingSig(null)}>Zrušit</Button>
              <Button variant="primary" onClick={() => void signAll()}>Podepsat</Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>Podpis se vloží na místo zhotovitele / vystavitele do:</p>
          <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
            <li>této faktury (pak ji ulož),</li>
            {toSign.map((d) => (
              <li key={d.id}>{d.title} — uloží se rovnou do archivu</li>
            ))}
          </ul>
          <img src={pendingSig} alt="Podpis" style={{ maxHeight: 70, background: '#fff', padding: 4, borderRadius: 4 }} />
          <p style={{ fontSize: 12, color: 'var(--sd-text-dim)', marginBottom: 0 }}>
            Podpis nahradí případný dosavadní podpis zhotovitele v těchto dokladech. Nabídka
            a objednávka se nepodepisují.
          </p>
        </Modal>
      )}
    </div>
  );
}
